import { useEffect, useState } from "react";
import API from "../../services/api";
import {
  Pagination,
  Modal,
  Input,
  Typography,
  Spin,
  Button,
  App,
  Empty,
} from "antd";
import {
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import "./AdminBusinessApprovals.css";

const { Title, Text } = Typography;

function AdminBusinessApprovals() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectReasonText, setRejectReasonText] = useState("");
  const [selectedBusinessId, setSelectedBusinessId] = useState(null);

  const { message } = App.useApp();

  /* ── Helpers ──────────────────────────────────────────────── */
  const showRejectDialog = (id) => {
    setSelectedBusinessId(id);
    setRejectModalVisible(true);
  };

  const hideRejectDialog = () => {
    setRejectModalVisible(false);
    setRejectReasonText("");
    setSelectedBusinessId(null);
  };

  /* ── Fetch ────────────────────────────────────────────────── */
  const loadPendingBusinesses = async (page = 1) => {
    try {
      setLoading(true);
      const res = await API.get(`/businesses/pending/all?page=${page}&limit=15`);
      setBusinesses(res.data.businesses || []);
      setTotal(res.data.total || 0);
      setCurrentPage(res.data.currentPage || 1);
    } catch {
      message.error("Failed to load pending businesses");
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPendingBusinesses(currentPage);
  }, [currentPage]);

  /* ── Actions ──────────────────────────────────────────────── */
  const approveBusiness = async (id) => {
    try {
      await API.put(`/businesses/approve/${id}`);
      message.success("Business approved successfully");
      loadPendingBusinesses(currentPage);
    } catch {
      message.error("Approval failed");
    }
  };

  const submitRejectReason = async () => {
    try {
      await API.put(`/businesses/reject/${selectedBusinessId}`, {
        reason: rejectReasonText,
      });
      message.success("Business rejected with reason");
      hideRejectDialog();
      loadPendingBusinesses(currentPage);
    } catch (err) {
      message.error(err.response?.data?.message || "Reject failed");
    }
  };

  /* ── Loader ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="bap-loading-center">
        <Spin size="large" />
      </div>
    );
  }

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div className="bap-app-container">
      {/* HEADER */}
      <div className="bap-dashboard-header">
        <div className="bap-dashboard-header__main">
          <Title level={3} className="bap-dashboard-header__main-title">
            Business Approvals
          </Title>
          <Text className="bap-dashboard-header__main-desc">
            Review and approve business listings
          </Text>
        </div>

        <div className="bap-dashboard-header__counter">
          <span className="bap-dashboard-header__counter-value">
            {businesses.length}
          </span>
          <span className="bap-dashboard-header__counter-text">
            Pending Review
          </span>
        </div>
      </div>

      {/* CONTENT */}
      {businesses.length === 0 ? (
        <Empty description="No pending requests" className="bap-no-data" />
      ) : (
        <div className="bap-grid-container">
          {businesses.map((business) => (
            <div
              key={business._id}
              className="bap-business-card"
              onClick={() => navigate(`/business/${business.slug}`)}
            >
              {/* Image */}
              <div className="bap-business-card__banner">
                <img
                  alt={business.companyName}
                  src={
                    business.logo ||
                    business.images?.[0] ||
                    "https://placehold.co/400x250?text=No+Logo"
                  }
                  className="bap-business-card__banner-img"
                />

                <button
                  className="bap-business-card__inspect-btn"
                  aria-label="View business"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/business/${business.slug}`);
                  }}
                >
                  <EyeOutlined />
                </button>

                <div className="bap-business-card__pending-badge">
                  <span className="bap-business-card__pending-indicator" />
                  Pending
                </div>
              </div>

              {/* Body */}
              <div className="bap-business-card__details">
                <h5
                  className="bap-business-card__company-name"
                  title={business.companyName}
                >
                  {business.companyName}
                </h5>

                <div className="bap-business-card__sector-tag">
                  <span>{business.industry || "N/A"}</span>
                </div>

                <div className="bap-business-card__info-bar">
                  <span>
                    {business.location?.city}, {business.location?.state}
                  </span>
                  <span className="bap-business-card__info-separator">•</span>
                  <span>{business.companySize || "N/A"}</span>
                  <span className="bap-business-card__info-separator">•</span>
                  <span>{business.type || "Business"}</span>
                </div>

                <div className="bap-business-card__decision-panel">
                  <Button
                    type="primary"
                    className="bap-decision-btn bap-decision-btn--positive"
                    icon={<CheckCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      approveBusiness(business._id);
                    }}
                  >
                    Approve
                  </Button>

                  <Button
                    danger
                    className="bap-decision-btn bap-decision-btn--negative"
                    icon={<CloseCircleOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      showRejectDialog(business._id);
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      {businesses.length > 0 && (
        <div className="bap-page-navigator">
          <Pagination
            current={currentPage}
            total={total}
            pageSize={15}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* REJECT MODAL */}
      <Modal
        title="Reject Business"
        open={rejectModalVisible}
        okText="Confirm Rejection"
        cancelText="Cancel"
        okButtonProps={{ disabled: !rejectReasonText.trim(), danger: true }}
        onOk={submitRejectReason}
        onCancel={hideRejectDialog}
        className="bap-reject-panel"
      >
        <div className="bap-reject-panel__form">
          <Text strong className="bap-reject-panel__form-label">
            Rejection Reason
          </Text>

          <Input.TextArea
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            value={rejectReasonText}
            onChange={(e) => setRejectReasonText(e.target.value)}
            className="bap-reject-panel__form-field"
          />

          {!rejectReasonText.trim() && (
            <p className="bap-reject-panel__form-error">
              Reason is required
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminBusinessApprovals;